import { useI18n } from '@ecom/i18n';
import { type ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';

import { useMySecurityQuery } from '../api';

const DISMISSED_KEY = 'souk-merchant-last-signin-dismissed';

function wasDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * On arrival: "Last sign-in: ..." - the time before THIS one - so that a sign-in the person does not recognise is seen the day it
 * happens. Dismissed for the rest of the browser session (sessionStorage, never anything sensitive in it). Shows nothing for a first
 * sign-in, while loading, or when the history cannot be read: a notice that is not sure of itself stays silent.
 */
export function LastSignInNotice(): ReactElement | null {
  const { t } = useI18n();
  const query = useMySecurityQuery();
  const [dismissed, setDismissed] = useState(wasDismissed);

  const previous = query.data?.previousSignIn ?? null;
  if (dismissed || previous === null) return null;

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-accentSoft px-6 py-2 text-sm text-accentInk"
    >
      <p>
        {t('account.lastSignIn.notice', { date: new Date(previous).toLocaleString('fr-TN') })}{' '}
        <Link to="/account" className="font-medium underline">
          {t('account.nav')}
        </Link>
        <span className="ms-2 text-xs opacity-80">{t('account.lastSignIn.hint')}</span>
      </p>
      <button
        type="button"
        className="text-xs font-medium underline"
        onClick={() => {
          try {
            window.sessionStorage.setItem(DISMISSED_KEY, '1');
          } catch {
            // The notice simply comes back next time: harmless.
          }
          setDismissed(true);
        }}
      >
        {t('account.lastSignIn.dismiss')}
      </button>
    </div>
  );
}
